import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { API_ENDPOINTS } from "./api/config";
import './sign_in.css';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("verifying");
  const hasFetched = useRef(false);
  const [message, setMessage] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const navigate = useNavigate();

  const token = searchParams.get("token");

  const verifyToken = async (tokenToVerify) => {
    setStatus("verifying");
    try {
      const response = await fetch(`${API_ENDPOINTS.VERIFY}?token=${tokenToVerify}`);
      const data = await response.json();
      if (response.ok) {
        setStatus("success");
        setMessage(data.message);
      } else {
        setStatus("error");
        setMessage(data.message);
      }
    } catch (err) {
      setStatus("error");
      setMessage("Server connection failed.");
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    if (token) verifyToken(token);
    else { setStatus("error"); setMessage("No token provided."); }
  }, [token]);

  const handleResend = async (e) => {
    e.preventDefault();
    setStatus("resending"); // This triggers the "Sending..." button state
    
    try {
        const res = await fetch(API_ENDPOINTS.RESENDVERIFICATION, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resendEmail }) 
        });

        const data = await res.json();

        if (res.ok) {
            setStatus("sendsuccess");
            setMessage(data.message);
        } else {
            if(data.responsecode === 400){
                setStatus("alreadyactive");
                setMessage(data.message || "This account is already verified.");
            }
            else{
                setStatus("error");
                setMessage(data.message || "We couldn't resend the link.");
            }
        }
    } catch (err) {
        setStatus("error");
        setMessage("Failed to connect to the server. Please try again later.");
    }
    };

  return (
    <div className="si-root">
      {/* LEFT PANEL — Content */}
      <div className="si-left">
        <Link to="/" className="si-logo">Rembo<span>List</span></Link>

        <div className="si-form-area">
          <div className="si-eyebrow">Security ✦ Verification</div>

          {status === "verifying" && (
            <div className="jsd-fade">
              <h1 className="si-title">Verifying your<br /><em>account</em></h1>
              <p className="si-subtitle">Authenticating your credentials with the RemboList network. Please stay on this page.</p>
              <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}>
                <span className="si-spinner" style={{ borderColor: 'rgba(26,26,26,0.1)', borderTopColor: '#c9a84c', width: '40px', height: '40px' }} />
              </div>
            </div>
          )}
          {status === "resending" && (
            <div className="jsd-fade">
              <h1 className="si-title">Resending Verification Email</h1>
              <p className="si-subtitle">Please wait while we resend the verification link to your email.</p>
              <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}>
                <span className="si-spinner" style={{ borderColor: 'rgba(26,26,26,0.1)', borderTopColor: '#c9a84c', width: '40px', height: '40px' }} />
              </div>
            </div>
          )}

          {status === "success" && (
            <div className="jsd-fade">
              <h1 className="si-title">Account<br /><em>Verified</em></h1>
              <p className="si-subtitle">{message || "Your identity has been confirmed. You now have full access to the platform."}</p>
              
              <div className="si-success-msg" style={{ marginBottom: '2rem' }}>
                 Your profile is now active and visible to the community.
              </div>

              <button className="si-btn" onClick={() => navigate("/signin")}>
                Sign In to Dashboard
              </button>
            </div>
          )}

          {status === "error" && (
            <div className="jsd-fade">
              <h1 className="si-title">Verification<br /><em>Failed</em></h1>
              <p className="si-subtitle">{message}</p>
              
              <div className="si-error" style={{ marginBottom: '2rem' }}>
                <span>⚠</span> This link may have expired (valid for 24 hours).
              </div>

              <div className="si-divider"><span>Request New Link</span></div>
              
              <form onSubmit={handleResend}>
                <div className="si-field">
                  <label className="si-label">Email Address</label>
                  <input 
                    type="email" 
                    className="si-input" 
                    placeholder="Enter your registered email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    required
                  />
                </div>
                <button className="si-btn" type="submit" disabled={status === "resending"}>
                  {status === "resending" ? "Sending..." : "Resend Verification Email"}
                </button>
              </form>

              {/* <button className="si-btn" onClick={() => navigate("/signup")}>
                Return to Registration
              </button> */}
              
              <div className="si-divider"><span>or</span></div>
              
              <div className="si-switch">
                Think this is a mistake? <Link to="/contact">Contact Support</Link>
              </div>
            </div>
          )}

          {status === "sendsuccess" && (
            <div className="jsd-fade">
              <h1 className="si-title">Verification<br /><em>Sent</em></h1>
              <p className="si-subtitle">{message || "If an account with that email exists, a new verification email has been sent to your inbox."}</p>
              
              <div className="si-success-msg" style={{ marginBottom: '2rem' }}>
                 Kindly check your inbox (and spam folder) for the verification email.
              </div>
            </div>
          )}
          {status === "alreadyactive" && (
            <div className="jsd-fade">
              <h1 className="si-title">Account<br /><em>is already Verified</em></h1>
              <p className="si-subtitle">{message || "Your identity has already been confirmed. You have full access to the platform."}</p>
              
              <div className="si-success-msg" style={{ marginBottom: '2rem' }}>
                 Your identity has already been confirmed. You have full access to the platform.
              </div>

              <button className="si-btn" onClick={() => navigate("/signin")}>
                Sign In to Dashboard
              </button>
            </div>
          )}
        </div>

        <p className="si-footer-note">
          Secure verification powered by RemboList Security Protocol v3.0
        </p>
      </div>

      {/* RIGHT PANEL — Brand Visual */}
      <div className="si-right">
        <div className="si-right-inner">
          <div className="si-panel-tag">Trust & Safety</div>
          <h2 className="si-panel-title">Protecting the<br /><em>beauty community</em></h2>
          <p className="si-panel-text">
            We verify every member to ensure RemboList remains a professional, 
            secure environment for Kenya's top beauty talent and premier salons.
          </p>
          <ul className="si-perks">
            <li><span className="si-perk-dot" /> Verified accounts receive 4x more engagement</li>
            <li><span className="si-perk-dot" /> Access to premium employer listings</li>
            <li><span className="si-perk-dot" /> Enhanced profile visibility in search results</li>
          </ul>
        </div>
      </div>
    </div>
  );
}